// @flow
import React from 'react'
import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField
} from '@material-ui/core'
import { isPostalCode, isEmpty, isEmail } from 'validator'
import { map, each } from 'lodash/collection'
import i18n from 'i18next'
import AddressInvoiceForm from '../AddressInvoiceForm'
import styles, { Wrapper, TitleBox } from './styles'
import Button from '../../../../master/components/styled/ButtonBase'

type Props = {
  postAddress: Function,
  navigateTo: Function,
  isAtCheckout?: boolean,
  queryProps: Object
}

type FormField = {
  value: string,
  isRequired?: boolean,
  error?: boolean,
  isValid?: Function
}

type State = {
  formFields: {
    alias: FormField,
    city: FormField,
    company: FormField,
    details: FormField,
    invoiceEmail: FormField,
    invoiceFirstSurname: FormField,
    invoiceName: FormField,
    invoicePersonType: FormField,
    invoiceSecondSurname: FormField,
    lift: FormField,
    name: FormField,
    nif: FormField,
    number: FormField,
    phone: FormField,
    postalCode: FormField,
    street: FormField
  },
  wantsInvoice: boolean,
  wantsInvoiceLegal: boolean,
  wantsInvoiceNatural: boolean
}

class AddressForm extends React.Component<Props, State> {
  state = {
    formFields: {
      alias: {
        error: false,
        isRequired: true,
        isValid: (alias: string) => !isEmpty(alias),
        value: ''
      },
      city: {
        error: false,
        isRequired: true,
        isValid: (city: string) => !isEmpty(city),
        value: ''
      },
      company: {
        error: false,
        isRequired: false,
        isValid: (company: string) => !isEmpty(company),
        value: ''
      },
      details: {
        error: false,
        isRequired: false,
        isValid: (details: string) => !isEmpty(details),
        value: ''
      },
      invoiceEmail: {
        error: false,
        isRequired: false,
        isValid: (invoiceEmail: string) => isEmail(invoiceEmail),
        value: ''
      },
      invoiceFirstSurname: {
        error: false,
        isRequired: false,
        isValid: (invoiceFirstSurname: string) => !isEmpty(invoiceFirstSurname),
        value: ''
      },
      invoiceName: {
        error: false,
        isRequired: false,
        isValid: (invoiceName: string) => !isEmpty(invoiceName),
        value: ''
      },
      invoicePersonType: {
        error: false,
        isRequired: false,
        isValid: (invoicePersonType: string) => !isEmpty(invoicePersonType),
        value: ''
      },
      invoiceSecondSurname: {
        error: false,
        isRequired: false,
        isValid: (invoiceSecondSurname: string) =>
          !isEmpty(invoiceSecondSurname),
        value: ''
      },
      lift: {
        error: false,
        isRequired: true,
        isValid: (lift: string) => !isEmpty(lift),
        value: ''
      },
      name: {
        error: false,
        isRequired: true,
        isValid: (name: string) => !isEmpty(name),
        value: ''
      },
      nif: {
        error: false,
        isRequired: false,
        isValid: (nif: string) => nif.length === 9,
        value: ''
      },
      number: {
        error: false,
        isRequired: true,
        isValid: (number: string) => !isEmpty(number),
        value: ''
      },
      phone: {
        error: false,
        isRequired: true,
        isValid: (phone: string) => !isEmpty(phone),
        value: ''
      },
      postalCode: {
        error: false,
        isRequired: true,
        isValid: (postalCode: string) => isPostalCode(postalCode, 'ES'),
        value: ''
      },
      street: {
        error: false,
        isRequired: true,
        isValid: (street: string) => !isEmpty(street),
        value: ''
      }
    },
    wantsInvoice: false,
    wantsInvoiceNatural: false,
    wantsInvoiceLegal: false
  }

  static defaultProps: Object

  onInvoiceChange: Function = (event: Object) => {
    this.setState(
      {
        ...this.state,
        wantsInvoice: !this.state.wantsInvoice,
        formFields: {
          ...this.state.formFields,
          invoicePersonType: {
            ...this.state.formFields['invoicePersonType'],
            isRequired: !this.state.wantsInvoice,
            error: false
          }
        }
      },
      () => {
        !this.state.wantsInvoice &&
          this.setState({
            ...this.state,
            wantsInvoiceLegal: false,
            wantsInvoiceNatural: false,
            formFields: {
              ...this.state.formFields,
              ...[
                'invoicePersonType',
                'company',
                'invoiceEmail',
                'invoiceFirstSurname',
                'invoiceName',
                'nif'
              ].reduce(
                (memo, key) => ({
                  ...memo,
                  [key]: {
                    ...this.state.formFields[key],
                    value: '',
                    isRequired: false
                  }
                }),
                {}
              )
            }
          })
      }
    )
  }

  onInvoicePersonTypeChange: Function = (event: Object) => {
    const mapFormFieldValueToState = (name: string, value: any) => ({
      ...this.state.formFields,
      [name]: { ...this.state.formFields[name], value }
    })

    const setRequiredFields = (requiredFields: Array<string>) => {
      return requiredFields.reduce(
        (memo, field) => ({
          ...memo,
          [field]: {
            ...this.state.formFields[field],
            isRequired: true,
            error: false
          }
        }),
        {}
      )
    }

    switch (event.target.value) {
      case 'natural':
        return this.setState({
          ...this.state,
          wantsInvoiceNatural: true,
          wantsInvoiceLegal: false,
          formFields: {
            ...mapFormFieldValueToState(event.target.name, event.target.value),
            ...setRequiredFields([
              'invoiceName',
              'invoiceFirstSurname',
              'invoiceEmail',
              'nif'
            ])
          }
        })

      case 'legal':
        return this.setState({
          ...this.state,
          wantsInvoiceNatural: false,
          wantsInvoiceLegal: true,
          formFields: {
            ...mapFormFieldValueToState(event.target.name, event.target.value),
            ...setRequiredFields(['company', 'invoiceEmail', 'nif'])
          }
        })

      default:
        return null
    }
  }

  onInputChange: Function = (event: Object) => {
    this.setState({
      ...this.state,
      formFields: {
        ...this.state.formFields,
        [event.target.name]: {
          ...this.state.formFields[event.target.name],
          value: event.target.value
        }
      }
    })
  }

  handleInputErrors: Function = (formFields: Object) => {
    each(Object.keys(formFields), fieldKey => {
      const field = formFields[fieldKey]

      field.isRequired &&
        this.setState(state => ({
          formFields: {
            ...state.formFields,
            [fieldKey]: {
              ...state.formFields[fieldKey],
              error: !field.isValid(field.value)
            }
          }
        }))
    })
  }

  onSubmit: Function = (event: Object) => {
    event.preventDefault()

    map(this.state.formFields, field => field.isRequired && !field.value).some(
      field => field === true
    )
      ? this.handleInputErrors(this.state.formFields)
      : this.props.postAddress(
        Object.keys(this.state.formFields).reduce(
          (memo, fieldKey) => ({
            ...memo,
            [fieldKey]: isEmpty(this.state.formFields[fieldKey].value)
              ? null
              : this.state.formFields[fieldKey].value
          }),
          {}
        )
      ) &&
        this.props.navigateTo &&
        this.props.navigateTo(this.props.queryProps.path)
  }

  render () {
    return (
      <Grid
        item
        xs={12}
        style={this.props.isAtCheckout ? undefined : styles.container}
      >
        {this.props.isAtCheckout && (
          <TitleBox>{`+${i18n.t('account.personalTab.addAddress')}`}</TitleBox>
        )}
        <Wrapper checkout={this.props.isAtCheckout}>
          <form onSubmit={this.onSubmit}>
            <Grid container spacing={16}>
              <Grid item xs={12} sm={this.props.isAtCheckout ? 6 : 12}>
                <TextField
                  error={this.state.formFields.alias.error}
                  helperText={
                    this.state.formFields.alias.error &&
                    i18n.t('checkout.error.alias')
                  }
                  id='delivery-alias'
                  label={i18n.t('checkout.label.alias')}
                  name='alias'
                  value={this.state.formFields.alias.value}
                  onChange={this.onInputChange}
                  margin='none'
                  variant='outlined'
                  required={this.state.formFields.alias.isRequired}
                  type='text'
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={this.props.isAtCheckout ? 6 : 12}>
                <TextField
                  error={this.state.formFields.name.error}
                  helperText={
                    this.state.formFields.name.error &&
                    i18n.t('checkout.error.nameSurname')
                  }
                  id='delivery-name'
                  label={i18n.t('checkout.label.nameSurname')}
                  name='name'
                  value={this.state.formFields.name.value}
                  onChange={this.onInputChange}
                  margin='none'
                  variant='outlined'
                  required
                  type='text'
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={this.props.isAtCheckout ? 6 : 12}>
                <TextField
                  error={this.state.formFields.street.error}
                  helperText={
                    this.state.formFields.street.error &&
                    i18n.t('checkout.error.street')
                  }
                  id='delivery-street'
                  label={i18n.t('checkout.label.street')}
                  name='street'
                  value={this.state.formFields.street.value}
                  onChange={this.onInputChange}
                  margin='none'
                  variant='outlined'
                  required={this.state.formFields.street.isRequired}
                  type='text'
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={this.props.isAtCheckout ? 6 : 12}>
                <TextField
                  error={this.state.formFields.number.error}
                  helperText={
                    this.state.formFields.number.error &&
                    i18n.t('checkout.error.number')
                  }
                  id='delivery-number'
                  label={i18n.t('checkout.label.number')}
                  name='number'
                  value={this.state.formFields.number.value}
                  onChange={this.onInputChange}
                  margin='none'
                  variant='outlined'
                  required={this.state.formFields.number.isRequired}
                  type='text'
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={this.props.isAtCheckout ? 6 : 12}>
                <TextField
                  id='delivery-details'
                  label={i18n.t('checkout.label.details')}
                  name='details'
                  value={this.state.formFields.details.value}
                  onChange={this.onInputChange}
                  margin='none'
                  variant='outlined'
                  type='text'
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={this.props.isAtCheckout ? 6 : 12}>
                <FormControl
                  fullWidth
                  margin='none'
                  required={this.state.formFields.lift.isRequired}
                  variant='outlined'
                  error={this.state.formFields.lift.error}
                >
                  <InputLabel htmlFor='delivery-lift'>
                    {i18n.t('checkout.label.lift')}
                  </InputLabel>
                  <Select
                    value={this.state.formFields.lift.value}
                    onChange={this.onInputChange}
                    input={
                      <OutlinedInput
                        labelWidth={168}
                        name='lift'
                        id='delivery-lift'
                      />
                    }
                    MenuProps={{
                      getContentAnchorEl: null,
                      anchorOrigin: { vertical: 'bottom', horizontal: 'left' }
                    }}
                  >
                    <MenuItem value={'yes'}>Sí</MenuItem>
                    <MenuItem value={'no'}>No</MenuItem>
                  </Select>
                  {this.state.formFields.lift.error && (
                    <FormHelperText error>
                      {i18n.t('checkout.error.lift')}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={this.props.isAtCheckout ? 6 : 12}>
                <TextField
                  error={this.state.formFields.postalCode.error}
                  helperText={
                    this.state.formFields.postalCode.error &&
                    i18n.t('checkout.error.postalCode')
                  }
                  id='delivery-postalcode'
                  label={i18n.t('checkout.label.postalCode')}
                  name='postalCode'
                  value={this.state.formFields.postalCode.value}
                  onChange={this.onInputChange}
                  margin='none'
                  variant='outlined'
                  required={this.state.formFields.postalCode.isRequired}
                  type='number'
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={this.props.isAtCheckout ? 6 : 12}>
                <TextField
                  error={this.state.formFields.city.error}
                  helperText={
                    this.state.formFields.city.error &&
                    i18n.t('checkout.error.city')
                  }
                  id='delivery-city'
                  label={i18n.t('checkout.label.city')}
                  name='city'
                  value={this.state.formFields.city.value}
                  onChange={this.onInputChange}
                  margin='none'
                  variant='outlined'
                  required={this.state.formFields.city.isRequired}
                  type='text'
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={this.props.isAtCheckout ? 6 : 12}>
                <TextField
                  error={this.state.formFields.phone.error}
                  helperText={
                    this.state.formFields.phone.error &&
                    i18n.t('checkout.error.phone')
                  }
                  id='delivery-telephone'
                  label={i18n.t('checkout.label.phone')}
                  name='phone'
                  value={this.state.formFields.phone.value}
                  onChange={this.onInputChange}
                  margin='none'
                  variant='outlined'
                  required={this.state.formFields.phone.isRequired}
                  type='number'
                  fullWidth
                />
              </Grid>
              {/* <Grid item xs={12} sm={this.props.isAtCheckout ? 6 : 12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={this.state.wantsInvoice}
                      onChange={this.onInvoiceChange}
                      value={`${String(this.state.wantsInvoice)}`}
                    />
                  }
                  label={i18n.t('checkout.label.includeBilling')}
                />
              </Grid> */}
              {this.state.wantsInvoice && (
                <Grid item xs={12} sm={this.props.isAtCheckout ? 6 : 12}>
                  <AddressInvoiceForm
                    wantsInvoiceLegal={this.state.wantsInvoiceLegal}
                    wantsInvoiceNatural={this.state.wantsInvoiceNatural}
                    company={this.state.formFields.company}
                    invoiceEmail={this.state.formFields.invoiceEmail}
                    invoiceFirstSurname={
                      this.state.formFields.invoiceFirstSurname
                    }
                    invoiceName={this.state.formFields.invoiceName}
                    invoicePersonType={this.state.formFields.invoicePersonType}
                    invoiceSecondSurname={
                      this.state.formFields.invoiceSecondSurname
                    }
                    nif={this.state.formFields.nif}
                    onInvoicePersonTypeChange={this.onInvoicePersonTypeChange}
                    onInputChange={this.onInputChange}
                  />
                </Grid>
              )}
            </Grid>
            <Grid container spacing={16} style={styles.button}>
              <Grid item xs={this.props.isAtCheckout ? 12 : 6}>
                <Button onClick={this.onSubmit} type='submit'>
                  {i18n.t('account.personalTab.save')}
                </Button>
              </Grid>
              {!this.props.isAtCheckout && (
                <Grid item xs={6}>
                  <Button
                    secondaryColor
                    onClick={() =>
                      this.props.navigateTo(this.props.queryProps.path)
                    }
                  >
                    {i18n.t('account.personalTab.cancel')}
                  </Button>
                </Grid>
              )}
            </Grid>
          </form>
        </Wrapper>
      </Grid>
    )
  }
}

export default AddressForm
